import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { paqueteService, PaqueteCreate } from '../../services/paqueteService';
import { sucursalService } from '../../services/sucursalService';
import { clienteService, Cliente } from '../../services/clienteService';
import { transferenciaService } from '../../services/transferenciaService';
import ClienteForm from '../Clientes/ClienteForm';

interface PaqueteFormProps {
  onClose: () => void;
}

const PaqueteForm = ({ onClose }: PaqueteFormProps) => {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState<'interno' | 'externo'>('externo');
  const [showClienteForm, setShowClienteForm] = useState(false);

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data: clientesData } = useQuery(
    'clientes',
    () => clienteService.getAll(1, 200)
  );

  const { data: transferenciasData } = useQuery(
    ['transferencias', 'pendiente'],
    () => transferenciaService.getAll(1, 100, 'pendiente'),
    { enabled: tipo === 'interno' }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<PaqueteCreate>({
    defaultValues: {
      tipo: 'externo',
      sucursalId: 0,
      remitenteNombre: '',
      destinatarioNombre: '',
      destinatarioTelefono: '',
      destinatarioDireccion: ''
    }
  });

  const clienteId = watch('clienteId');

  const createMutation = useMutation(paqueteService.create, {
    onSuccess: () => {
      toast.success('Paquete registrado correctamente');
      queryClient.invalidateQueries(['paquetes']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al registrar paquete');
    }
  });

  const onSubmit = (data: PaqueteCreate) => {
    createMutation.mutate({
      ...data,
      tipo: tipo
    });
  };

  // Si se selecciona un cliente, autocompletar datos del destinatario
  const applyClienteToDestinatario = (cliente: Cliente) => {
    if (cliente.nombre) {
      setValue('destinatarioNombre', cliente.nombre, { shouldValidate: true });
    }
    if (cliente.telefono) {
      setValue('destinatarioTelefono', cliente.telefono, { shouldValidate: true });
    }
    if (cliente.email) {
      setValue('destinatarioEmail', cliente.email);
    }
    if (cliente.direccion) {
      setValue('destinatarioDireccion', cliente.direccion, { shouldValidate: true });
    }
  };

  const handleClienteChange = (clienteId: number) => {
    const cliente = clientesData?.data.clientes.find(c => c.id === clienteId);
    if (cliente) {
      applyClienteToDestinatario(cliente);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Registrar Nuevo Paquete</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tipo de paquete */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Paquete *
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="externo"
                  checked={tipo === 'externo'}
                  onChange={(e) => setTipo(e.target.value as 'externo')}
                  className="mr-2"
                />
                Externo (Courier)
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="interno"
                  checked={tipo === 'interno'}
                  onChange={(e) => setTipo(e.target.value as 'interno')}
                  className="mr-2"
                />
                Interno (Transferencia)
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Sucursal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal *
              </label>
              <select
                {...register('sucursalId', { 
                  required: 'Sucursal es requerida',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Selecciona una sucursal' }
                })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Selecciona sucursal</option>
                {sucursalesData?.data.sucursales.map((sucursal) => (
                  <option key={sucursal.id} value={sucursal.id}>
                    {sucursal.name}
                  </option>
                ))}
              </select>
              {errors.sucursalId && (
                <p className="text-red-500 text-sm mt-1">{errors.sucursalId.message}</p>
              )}
            </div>

            {/* Cliente (opcional) */}
            {tipo === 'externo' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente (Opcional)
                </label>
                <div className="flex space-x-2">
                  <select
                    {...register('clienteId', { valueAsNumber: true })}
                    onChange={(e) => handleClienteChange(Number(e.target.value))}
                    className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={0}>Sin cliente</option>
                    {clientesData?.data.clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre} {cliente.email ? `- ${cliente.email}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowClienteForm(true)}
                    className="px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                  >
                    + Nuevo
                  </button>
                </div>
              </div>
            )}

            {/* Transferencia (solo si es interno) */}
            {tipo === 'interno' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transferencia (Opcional)
                </label>
                <select
                  {...register('transferenciaId', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Sin transferencia</option>
                  {transferenciasData?.data.transferencias.map((transferencia) => (
                    <option key={transferencia.id} value={transferencia.id}>
                      #{transferencia.id} - {transferencia.sucursalOrigen?.name} → {transferencia.sucursalDestino?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Remitente */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Datos del Remitente</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  {...register('remitenteNombre', { required: 'Nombre del remitente es requerido' })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.remitenteNombre && (
                  <p className="text-red-500 text-sm mt-1">{errors.remitenteNombre.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  {...register('remitenteTelefono')}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  {...register('remitenteEmail')}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  {...register('remitenteDireccion')}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Destinatario */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Datos del Destinatario</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  {...register('destinatarioNombre', { required: 'Nombre del destinatario es requerido' })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.destinatarioNombre && (
                  <p className="text-red-500 text-sm mt-1">{errors.destinatarioNombre.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  {...register('destinatarioTelefono', { required: 'Teléfono del destinatario es requerido' })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.destinatarioTelefono && (
                  <p className="text-red-500 text-sm mt-1">{errors.destinatarioTelefono.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  {...register('destinatarioEmail')}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección *
                </label>
                <input
                  {...register('destinatarioDireccion', { required: 'Dirección del destinatario es requerida' })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.destinatarioDireccion && (
                  <p className="text-red-500 text-sm mt-1">{errors.destinatarioDireccion.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Información adicional */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Información Adicional</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso (kg)
                </label>
                <input
                  type="number"
                  step="0.001"
                  {...register('peso', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensiones (LxAxA)
                </label>
                <input
                  {...register('dimensiones')}
                  placeholder="Ej: 30x20x15"
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Declarado
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('valorDeclarado', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ubicación Actual
              </label>
              <input
                {...register('ubicacionActual')}
                placeholder="Ej: Estante A-5"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <textarea
                {...register('notas')}
                rows={3}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Guardando...' : 'Registrar Paquete'}
            </button>
          </div>
        </form>
      </div>

      {showClienteForm && (
        <ClienteForm
          onClose={() => setShowClienteForm(false)}
          onSuccess={(nuevoCliente) => {
            // Actualizar lista de clientes en caché
            queryClient.invalidateQueries(['clientes']);
            // Seleccionar automáticamente el nuevo cliente
            setValue('clienteId', nuevoCliente.id, { shouldValidate: true });
            applyClienteToDestinatario(nuevoCliente);
            setShowClienteForm(false);
          }}
        />
      )}
    </div>
  );
};

export default PaqueteForm;

